import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Video, Image, Eye, EyeOff, MoveUp, MoveDown } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SalesShowcaseManagement = () => {
  const { toast } = useToast();
  const [showcases, setShowcases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingShowcase, setEditingShowcase] = useState(null);
  const [formData, setFormData] = useState({
    mediaType: 'video',
    mediaUrl: '',
    thumbnail: '',
    title: '',
    description: '',
    ctaText: '',
    ctaLink: '',
    isActive: true
  });

  useEffect(() => {
    fetchShowcases();
  }, []);

  const fetchShowcases = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sales-showcase`);
      if (response.ok) {
        const data = await response.json();
        setShowcases(data);
      }
    } catch (error) {
      console.error('Error fetching showcases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingShowcase
        ? `${BACKEND_URL}/api/admin/sales-showcase/${editingShowcase.showcaseId}`
        : `${BACKEND_URL}/api/admin/sales-showcase`;
      
      const response = await fetch(url, {
        method: editingShowcase ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast({ title: `Showcase ${editingShowcase ? 'updated' : 'created'} successfully` });
        setShowModal(false);
        setEditingShowcase(null);
        resetForm();
        fetchShowcases();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Operation failed" });
    }
  };

  const handleDelete = async (showcase) => {
    if (!window.confirm('Delete this showcase?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sales-showcase/${showcase.showcaseId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({ title: "Showcase deleted successfully" });
        fetchShowcases();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to delete" });
    }
  };

  const toggleActive = async (showcase) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/sales-showcase/${showcase.showcaseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !showcase.isActive })
      });

      if (response.ok) {
        toast({ title: "Status updated" });
        fetchShowcases();
      }
    } catch (error) {
      toast({ variant: "destructive", title: "Failed to update status" });
    }
  };

  const openEditModal = (showcase) => {
    setEditingShowcase(showcase);
    setFormData({
      mediaType: showcase.mediaType,
      mediaUrl: showcase.mediaUrl,
      thumbnail: showcase.thumbnail || '',
      title: showcase.title,
      description: showcase.description || '',
      ctaText: showcase.ctaText || '',
      ctaLink: showcase.ctaLink || '',
      isActive: showcase.isActive
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      mediaType: 'video',
      mediaUrl: '',
      thumbnail: '',
      title: '',
      description: '',
      ctaText: '',
      ctaLink: '',
      isActive: true
    });
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Sales Showcase</h2>
          <p className="text-slate-600">{showcases.length} total items</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Add Showcase
        </Button>
      </div>

      {/* Showcases List */}
      <div className="space-y-4">
        {showcases.map((showcase) => (
          <div key={showcase.showcaseId} className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-1">
                <div className={`w-20 h-20 rounded-lg flex items-center justify-center ${
                  showcase.mediaType === 'video' ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  {showcase.mediaType === 'video' ? (
                    <Video className="w-8 h-8 text-red-600" />
                  ) : (
                    <Image className="w-8 h-8 text-blue-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-slate-800">{showcase.title}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      showcase.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {showcase.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {showcase.description && (
                    <p className="text-sm text-slate-600 mb-2">{showcase.description}</p>
                  )}
                  <p className="text-sm text-slate-500 mb-2">
                    <strong>URL:</strong> {showcase.mediaUrl}
                  </p>
                  {showcase.ctaText && (
                    <p className="text-sm text-slate-500">
                      <strong>CTA:</strong> {showcase.ctaText} → {showcase.ctaLink}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button size="sm" variant="outline" onClick={() => toggleActive(showcase)}>
                  {showcase.isActive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEditModal(showcase)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(showcase)} className="text-red-600">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <h3 className="text-xl font-bold mb-4">{editingShowcase ? 'Edit' : 'Create'} Showcase</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Media Type</label>
                  <select
                    value={formData.mediaType}
                    onChange={(e) => setFormData({...formData, mediaType: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="video">Video</option>
                    <option value="image">Image</option>
                    <option value="banner">Banner</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.isActive}
                    onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
                    className="w-full px-4 py-2 border rounded-lg"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Media URL</label>
                <input
                  type="url"
                  value={formData.mediaUrl}
                  onChange={(e) => setFormData({...formData, mediaUrl: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="https://..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Thumbnail URL (optional)</label>
                <input
                  type="url"
                  value={formData.thumbnail}
                  onChange={(e) => setFormData({...formData, thumbnail: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">CTA Text</label>
                  <input
                    type="text"
                    value={formData.ctaText}
                    onChange={(e) => setFormData({...formData, ctaText: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="Learn More"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">CTA Link</label>
                  <input
                    type="url"
                    value={formData.ctaLink}
                    onChange={(e) => setFormData({...formData, ctaLink: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg"
                    placeholder="https://..."
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingShowcase(null); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingShowcase ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesShowcaseManagement;
